import { computed, onUnmounted, ref } from 'vue';
import { useApi } from '@shopexperts/shared';
import { extractServerMessage, extractStatus, resolveThrottleMessage } from './claimApiErrors';

/** Matches the API's 60s per-claim resend throttle. */
const RESEND_COOLDOWN_SECONDS = 60;

/**
 * Resend-verification action shared by the embedded claim form (duplicate-claim
 * state) and the verify page (expired/invalid-link recovery).
 *
 * The API deliberately returns a generic 200 whether or not an unverified claim
 * matched, so the success copy must stay hedged — never promise an email was sent.
 */
export function useResendClaimVerification() {
  const api = useApi();

  const isSending = ref(false);
  const cooldownSeconds = ref(0);
  const successMessage = ref('');
  const errorMessage = ref('');
  let cooldownInterval: ReturnType<typeof setInterval> | null = null;

  const canSend = computed(() => !isSending.value && cooldownSeconds.value <= 0);

  function stopCooldown() {
    if (cooldownInterval) {
      clearInterval(cooldownInterval);
      cooldownInterval = null;
    }
  }

  function startCooldown() {
    stopCooldown();
    cooldownSeconds.value = RESEND_COOLDOWN_SECONDS;
    cooldownInterval = setInterval(() => {
      cooldownSeconds.value -= 1;
      if (cooldownSeconds.value <= 0) stopCooldown();
    }, 1000);
  }

  async function send(expertSlug: string, email: string): Promise<boolean> {
    if (!canSend.value) return false;

    isSending.value = true;
    successMessage.value = '';
    errorMessage.value = '';

    try {
      await api.public.expertClaims.resendVerification({
        body: { expert_slug: expertSlug, email },
      });
      successMessage.value = `If a matching unverified claim exists, we've sent a new link to ${email}.`;
      startCooldown();
      return true;
    } catch (error) {
      if (extractStatus(error) === 429) {
        errorMessage.value = resolveThrottleMessage(error);
      } else {
        errorMessage.value = extractServerMessage(error)
          || 'Something went wrong while sending the email. Please try again.';
      }
      return false;
    } finally {
      isSending.value = false;
    }
  }

  onUnmounted(stopCooldown);

  return {
    isSending,
    cooldownSeconds,
    canSend,
    successMessage,
    errorMessage,
    send,
  };
}
